/**
 * ICE server configuration (STUN/TURN) for WebRTC calls.
 *
 * For PRIVACY the app uses NO ICE server by default: host
 * candidates are enough on the same network and we do not leak the user's IP to a
 * third-party STUN. For calls between different networks (symmetric NAT) an
 * OWN TURN relay is needed: it is enabled explicitly behind a local flag
 * (`localStorage["susurro:turn"]`), never by default.
 *
 * Flag format (JSON):
 *   { "enabled": true, "urls": ["turn:turn.example.com:3478"],
 *     "username": "user", "credential": "secret" }
 *
 * Any missing or invalid value, or one with `enabled !== true`, degrades to `[]`
 * (private behavior by default). Deploying/validating a real TURN is a
 * manual infrastructure operation (see docs/E2E_MESSAGING_GUIDE.md).
 */

export const TURN_CONFIG_KEY = "susurro:turn";

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null ? { ...value } : {};
}

/**
 * Effective ICE servers according to the local flag. Without a flag (or invalid) → `[]`.
 * The `storage` is injectable to test it without a browser.
 */
export function iceServersFromConfig(
  storage: Pick<Storage, "getItem"> | undefined = globalThis.localStorage,
): RTCIceServer[] {
  let raw: string | null = null;
  try {
    raw = storage?.getItem(TURN_CONFIG_KEY) ?? null;
  } catch {
    return [];
  }
  if (!raw) {
    return [];
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  const r = asRecord(parsed);
  if (r.enabled !== true || !Array.isArray(r.urls)) {
    return [];
  }
  const urls = r.urls.filter(
    (u): u is string => typeof u === "string" && /^(turns?|stuns?):/.test(u),
  );
  if (urls.length === 0) {
    return [];
  }
  const server: RTCIceServer = { urls };
  if (typeof r.username === "string" && typeof r.credential === "string") {
    return [{ ...server, username: r.username, credential: r.credential }];
  }
  return [server];
}
